import React from 'react';

import {
  ChecklistItem,
  MAX_CHECKLIST_ITEMS,
  addChecklistItems,
  deleteChecklistItem,
  getChecklist,
  updateChecklistItem,
} from '../../../../services/trips/api/tripsApi';
import './TripChecklist.css';

/**
 * The trip's checklist — what to bring, what to sort out.
 *
 * Distinct from the itinerary and deliberately so: an activity has a place and
 * a time, a checklist item has neither. "Pack sunscreen" is not a stop, and
 * putting it on the timeline would mean inventing a time for it.
 *
 * A SECTION here rather than a tab. Mobile has tabs because a phone shows one
 * pane at a time; this page is already a two-column workspace, so the list sits
 * in the main column under the itinerary where it can be read alongside it.
 *
 * Ticking is OPTIMISTIC — a packing list is worked through in bursts, and a
 * checkbox that waits for a round trip before it moves feels broken at that
 * pace. A failed write puts the tick back and says so.
 */
export const TripChecklist: React.FC<{ tripId: string; canEdit: boolean }> = ({
  tripId,
  canEdit,
}) => {
  const [items, setItems] = React.useState<ChecklistItem[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [isAdding, setIsAdding] = React.useState(false);
  const [busyItemId, setBusyItemId] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      setItems(await getChecklist(tripId));
      setError(null);
    } catch (caught: any) {
      setError(caught?.message || 'Could not load the checklist.');
    } finally {
      setIsLoading(false);
    }
  }, [tripId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const done = items.filter((item) => item.checked).length;
  const isFull = items.length >= MAX_CHECKLIST_ITEMS;

  const toggle = async (item: ChecklistItem) => {
    const next = !item.checked;
    // Optimistic: move now, reconcile after.
    setItems((current) =>
      current.map((row) => (row.itemId === item.itemId ? { ...row, checked: next } : row)),
    );
    setError(null);
    try {
      await updateChecklistItem(tripId, item.itemId, { checked: next });
    } catch (caught: any) {
      setItems((current) =>
        current.map((row) => (row.itemId === item.itemId ? { ...row, checked: !next } : row)),
      );
      setError(caught?.message || 'That change could not be saved.');
    }
  };

  const add = async (event: React.FormEvent) => {
    event.preventDefault();
    const text = title.trim();
    if (!text || isAdding) return;
    setIsAdding(true);
    setError(null);
    try {
      await addChecklistItems(tripId, [
        { title: text, ...(description.trim() ? { description: description.trim() } : {}) },
      ]);
      setTitle('');
      setDescription('');
      await load();
    } catch (caught: any) {
      setError(caught?.message || 'Could not add that item.');
    } finally {
      setIsAdding(false);
    }
  };

  const remove = async (item: ChecklistItem) => {
    setBusyItemId(item.itemId);
    setError(null);
    try {
      await deleteChecklistItem(tripId, item.itemId);
      await load();
    } catch (caught: any) {
      setError(caught?.message || 'Could not remove that item.');
    } finally {
      setBusyItemId(null);
    }
  };

  return (
    <section className="v2-trip-checklist" aria-label="Trip checklist">
      <header className="v2-trip-checklist__head">
        <h3 className="v2-trip-checklist__title">What to bring</h3>
        <span className="v2-trip-checklist__count">
          {items.length === 0 ? 'Nothing yet' : `${done} of ${items.length} packed`}
          {/* The remaining count only earns its place near the limit — at three
              of thirty it is noise. */}
          {items.length >= MAX_CHECKLIST_ITEMS - 5 &&
            (isFull ? ` · full (${MAX_CHECKLIST_ITEMS} max)` : ` · ${MAX_CHECKLIST_ITEMS - items.length} left`)}
        </span>
      </header>

      {error && <p className="v2-trip-checklist__error">{error}</p>}
      {isLoading && items.length === 0 && <p className="v2-trip-checklist__muted">Loading…</p>}

      <ul className="v2-trip-checklist__list">
        {items.map((item) => (
          <li
            key={item.itemId}
            className={`v2-trip-checklist__item ${item.checked ? 'v2-trip-checklist__item--done' : ''}`}
          >
            <label className="v2-trip-checklist__label">
              <input
                type="checkbox"
                checked={item.checked}
                disabled={!canEdit}
                onChange={() => void toggle(item)}
              />
              <span className="v2-trip-checklist__text">
                <span className="v2-trip-checklist__item-title">{item.title}</span>
                {item.description && (
                  <span className="v2-trip-checklist__item-note">{item.description}</span>
                )}
              </span>
            </label>
            {canEdit && (
              <button
                type="button"
                className="v2-trip-checklist__remove"
                onClick={() => void remove(item)}
                disabled={busyItemId === item.itemId}
                aria-label={`Remove ${item.title}`}
              >
                ×
              </button>
            )}
          </li>
        ))}
      </ul>

      {!isLoading && items.length === 0 && (
        <p className="v2-trip-checklist__muted">
          {canEdit
            ? 'Add what you need to bring — or ask the planner to suggest a list.'
            : 'Nothing on this trip’s list yet.'}
        </p>
      )}

      {canEdit && !isFull && (
        <form className="v2-trip-checklist__add" onSubmit={add}>
          <input
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Add an item — sunscreen, adapter, insurance…"
            aria-label="Item"
          />
          <input
            type="text"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Note (optional)"
            aria-label="Note"
          />
          <button type="submit" disabled={!title.trim() || isAdding}>
            {isAdding ? 'Adding…' : 'Add'}
          </button>
        </form>
      )}

      {canEdit && isFull && (
        <p className="v2-trip-checklist__muted">
          Remove something to add more — a trip holds up to {MAX_CHECKLIST_ITEMS} items.
        </p>
      )}
    </section>
  );
};

export default TripChecklist;
